/**
 * The ONLY routes a location viewer (shared per-location account, room
 * iPad) may reach — Task 3.1, docs/plans/2026-07-30-location-login.md.
 * Matched against the REGISTRY path pattern (route.path, e.g.
 * "/grow/knowledge/:slug"), never the live resolved URL — exact string
 * match, no prefix/wildcard logic, so a future route can never
 * accidentally satisfy this by sharing a path prefix.
 *
 * Fail-closed by construction: any path not in this set is blocked for a
 * location viewer, including every route added after this file was
 * written. Single source of truth for both route-guard enforcement
 * (RouteGateGuard.tsx) and nav visibility (Layout.tsx).
 *
 * Daily-log surface finding (Task 3.1): the kiosk-style daily-log WRITE
 * actions (food temp, cleaning check, medication/accident forms) exist
 * ONLY on the public, anonymous /kiosk/:locationSlug route today. The one
 * authenticated route that mentions "daily logs" — /daily-logs
 * (KioskWeekGrid) — is a manager-facing WEEKLY REVIEW surface (cross-day,
 * cross-group WeekGridPanel + a list/archive view of already-submitted
 * medication and accident forms), not a same-day action surface, and it
 * exposes data beyond one location/group. It is deliberately NOT
 * allowlisted. There is currently no authenticated in-app equivalent of
 * the kiosk daily-log actions to allow.
 */
import { matchPath } from "react-router-dom";

export const LOCATION_VIEWER_ALLOWED_PATHS: ReadonlySet<string> = new Set([
  "/grid",
  "/activities",
  "/grow/knowledge",
  "/grow/knowledge/:slug",
  "/parent-messages",
]);

/** Where a blocked location-viewer navigation redirects to. */
export const LOCATION_VIEWER_HOME_PATH = "/grid";

/** Registry-path-pattern match (route.path as declared in registry.tsx, e.g.
 * "/grow/knowledge/:slug") — used by RouteGateGuard, which already knows
 * which registry entry it's guarding. */
export function isLocationAllowedRoutePath(routePath: string): boolean {
  return LOCATION_VIEWER_ALLOWED_PATHS.has(routePath);
}

/**
 * Live-URL match — used by the router-level enforcer (LocationRouteEnforcer),
 * which only has `location.pathname` (e.g. "/grow/knowledge/some-slug"), not
 * a registry entry. RouteGateGuard only wraps `kind:"page"` routes — every
 * `kind:"redirect"` entry (e.g. /insights -> /) and the "*" NotFound
 * catch-all bypass it entirely, so a location viewer landing on one of those
 * needs this independent, route-kind-agnostic check (live-smoke finding,
 * Phase 3 follow-up). matchPath handles the ":slug" dynamic segment
 * correctly; `end: true` so "/grow/knowledge" doesn't accidentally also
 * match "/grow/knowledge/extra/segments".
 */
export function isLocationAllowedPathname(pathname: string): boolean {
  return [...LOCATION_VIEWER_ALLOWED_PATHS].some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
  );
}
